import { styled } from '@mui/material/styles';
import MarkdownRenderer from '_shared/_views/markdown/markdown-renderer';
import AssistantCitation from 'ai-assistant/citations/assistant-citation';
import Citations, { citationPanelId } from 'ai-assistant/citations/_views/citations';
import AssistantGrounding from 'ai-assistant/grounding/assistant-grounding';
import GroundingNotice from 'ai-assistant/grounding/_views/grounding-notice';
import React from 'react';
import { Components } from 'react-markdown';

/**
 * The markers the API emits. `[[n]]` attributes a claim to source n; `[[?]]` says the server
 * checked a claim against the span it cited and would not stand behind the attribution.
 *
 * Both are `[[...]]` rather than `[...]` because assistant answers are dense with markdown
 * links, and a marker sharing bracket syntax with `[text](url)` gets parsed as one.
 */
const CITATION_MARKER = /\[\[(\d{1,3}|\?)\]\]/g;
const MARKER_CLASS = 'assistant-citation-marker';
const UNVERIFIED_ORDINAL = '?';

/**
 * Turns each marker into a superscript the markdown renderer will hand back through its
 * `sup` override. The ordinal travels as the element's text, which survives sanitization
 * without needing a custom attribute.
 */
const withMarkerElements = (text: string) =>
  text.replace(CITATION_MARKER, (_match, ordinal: string) =>
    `<sup class="${MARKER_CLASS}">${ordinal}</sup>`);

const Marker = styled('button')(({ theme }) => ({
  font: 'inherit',
  fontSize: theme.typography.pxToRem(10),
  lineHeight: 1,
  verticalAlign: 'super',
  margin: '0 1px',
  padding: '1px 4px',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

/**
 * The unverified mark. Deliberately not a button: there is no span to open, because the
 * server withdrew the attribution rather than choosing a different one.
 */
const UnverifiedMarker = styled('span')(({ theme }) => ({
  font: 'inherit',
  fontSize: theme.typography.pxToRem(10),
  lineHeight: 1,
  verticalAlign: 'super',
  margin: '0 1px',
  padding: '1px 4px',
  borderRadius: '6px',
  cursor: 'help',
  color: theme.palette.warning.contrastText,
  backgroundColor: theme.palette.warning.main,
}));

const UNVERIFIED_LABEL = 'This statement is not supported by the source it cited.';

type CitedAnswerProps = {
  text: string;
  citations: AssistantCitation[];
  /**
   * What the server said it checked. Optional so a locally-built message - the user's own
   * turn, an optimistic echo - does not have to state a grounding it has no opinion about.
   */
  grounding?: AssistantGrounding;
};

/**
 * An assistant answer and the sources it cites, joined: clicking a marker opens the span
 * that claim came from.
 */
export default function CitedAnswer(props: Readonly<CitedAnswerProps>) {
  const { text, citations, grounding } = props;
  const [expandedOrdinal, setExpandedOrdinal] = React.useState<number | undefined>(undefined);
  // Ordinals are turn-local, and a conversation renders every answer into one document, so
  // the panel ids need something per-answer to stay unique across the page.
  const panelScope = React.useId();

  const citedOrdinals = new Set(citations.map((citation) => citation.ordinal));

  const revealCitation = (ordinal: number) => {
    setExpandedOrdinal(ordinal);
    // scrollIntoView is absent in jsdom and in some older embedded browsers; expanding
    // the panel is the part that matters, scrolling to it is a convenience.
    document.getElementById(citationPanelId(panelScope, ordinal))?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'nearest',
    });
  };

  const components: Components = {
    sup: (supProps) => {
      const className = supProps.className;
      const children = supProps.children;
      if (!className?.includes(MARKER_CLASS)) {
        return <sup className={className}>{children}</sup>;
      }

      const marker = String(children);
      if (marker === UNVERIFIED_ORDINAL) {
        return (
          <UnverifiedMarker role="note" aria-label={UNVERIFIED_LABEL} title={UNVERIFIED_LABEL}>
            ?
          </UnverifiedMarker>
        );
      }

      const ordinal = Number(marker);
      // A marker for a source that is not in the citation list has nothing to open.
      if (!citedOrdinals.has(ordinal)) {
        return null;
      }

      return (
        <Marker
          type="button"
          aria-label={`Show source ${ordinal}`}
          onClick={() => revealCitation(ordinal)}
        >
          {ordinal}
        </Marker>
      );
    },
  };

  return (
    <>
      <MarkdownRenderer components={components}>
        {withMarkerElements(text)}
      </MarkdownRenderer>
      {grounding && <GroundingNotice grounding={grounding} />}
      {citations.length > 0 && (
        <Citations
          sx={{ mt: 2, mb: 1 }}
          citations={citations}
          panelScope={panelScope}
          expandedOrdinal={expandedOrdinal}
          onExpandedOrdinalChange={setExpandedOrdinal}
        />
      )}
    </>
  );
}
